import React from 'react';

import { PortalConfigContext } from 'src/context';
import type { ResolvedSettingsPage } from 'src/features/settings/registry/settingsRegistry';
import { useSettingsVisibilityContext } from 'src/features/settings/registry/useSettingsVisibilityContext';
import { useSettingsSidebarNavigate } from 'src/features/settings/sidebar/useSettingsSidebarNavigate';
import { getClientSettingsTerm } from 'src/utils/clientSettingsPaths';

import { searchSettingsPages, SettingsSearchResult } from './settingsSearch';
import { useSettingsSearchState } from './SettingsSearchContext';

/** One row the keyboard can land on: a page, or a matching setting under it. */
export interface SettingsSearchOption {
  id: string;
  kind: 'page' | 'setting';
  label: string;
  page: ResolvedSettingsPage;
}

export const SETTINGS_SEARCH_RESULTS_ID = 'settings-search-results';

export const getSettingsSearchOptionId = (index: number) =>
  `settings-search-option-${index}`;

const toOptions = (results: SettingsSearchResult[]): SettingsSearchOption[] =>
  results.flatMap(({ page, hits }) => [
    { id: page.id, kind: 'page' as const, label: page.label, page },
    ...hits.map((hit) => ({
      id: `${page.id}:${hit}`,
      kind: 'setting' as const,
      label: hit,
      page,
    })),
  ]);

/** The current query's results, flattened for the keyboard, and a way to open one. */
export const useSettingsSearch = () => {
  const { query, setQuery, activeIndex, setActiveIndex } =
    useSettingsSearchState();
  const ctx = useSettingsVisibilityContext();
  const { features } = React.useContext(PortalConfigContext);
  const navigate = useSettingsSidebarNavigate();

  const results = searchSettingsPages({
    query,
    ctx,
    clientTerm: getClientSettingsTerm(features.customLabels),
  });
  const options = toOptions(results);

  // A setting opens its page; scrolling to the row is out of scope for v1.
  const select = (option: SettingsSearchOption) => {
    setQuery('');
    navigate(option.page.path);
  };

  return {
    query,
    setQuery,
    isSearching: query.trim().length > 0,
    results,
    options,
    activeIndex: Math.min(activeIndex, Math.max(options.length - 1, 0)),
    setActiveIndex,
    select,
  };
};
